import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CatalogEntryResponse, RoleSummaryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { SUPPORTED_LOCALES, useI18n } from '@/i18n/i18n-provider';
import { administrationApi } from '@/services/administration.api';
import { authorizationApi } from '@/services/authorization.api';
import { tenancyApi } from '@/services/tenancy.api';
import { filesApi } from '@/services/files.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';
import { LocationMapPicker } from '@/components/location-map-picker';
import { EntityLogo } from '@/components/entity-logo';
import { PersonTabPanel, PersonTabs, type PersonTabDef } from '../people/person-detail-tabs';
import { InviteMemberDialog } from './invite-member-dialog';
import { PastorsTab } from './pastors-tab';

/** Static for the session — the IANA database a browser ships doesn't change mid-visit. */
const TIMEZONES: readonly string[] =
  typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : ['UTC'];

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/** A `<select>` option list that always includes the currently-selected
 *  entry, even if it has since been archived out of the active picker —
 *  the same "current value survives even if it's no longer offered" rule
 *  the member-role `<select>` below already follows for a stale role key. */
function CatalogSelect({
  id,
  label,
  value,
  current,
  options,
  onChange,
  disabled,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly current: CatalogEntryResponse | null;
  readonly options: readonly CatalogEntryResponse[];
  readonly onChange: (value: string) => void;
  readonly disabled: boolean;
}) {
  const { t } = useI18n();
  const hasCurrentInOptions = current && options.some((option) => option.id === current.id);

  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="field-input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      >
        <option value="">{t('common.labels.none')}</option>
        {current && !hasCurrentInOptions && <option value={current.id}>{current.name}</option>}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function roleLabel(
  roleKey: string,
  roles: readonly RoleSummaryResponse[],
  t: (key: string, options?: { fallback?: string }) => string,
): string {
  const role = roles.find((candidate) => candidate.key === roleKey);
  if (!role) return roleKey;
  // A custom role's `nameKey` holds its literal, tenant-authored name, not
  // a translation key — see `RoleSummaryResponse`.
  return role.name ?? t(role.nameKey, { fallback: roleKey });
}

/** Tenant administration: edit the church's general profile, invite members, change roles, remove members. */
export function AdministrationPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [isInviting, setIsInviting] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [name, setName] = useState<string | null>(null);
  const [defaultLocale, setDefaultLocale] = useState<string | null>(null);
  const [defaultTimezone, setDefaultTimezone] = useState<string | null>(null);
  const [defaultCurrency, setDefaultCurrency] = useState<string | null>(null);
  const [denominationId, setDenominationId] = useState<string | null>(null);
  const [councilId, setCouncilId] = useState<string | null>(null);
  const [addressLine1, setAddressLine1] = useState<string | null>(null);
  const [addressLine2, setAddressLine2] = useState<string | null>(null);
  const [addressCity, setAddressCity] = useState<string | null>(null);
  const [addressRegion, setAddressRegion] = useState<string | null>(null);
  const [addressPostalCode, setAddressPostalCode] = useState<string | null>(null);
  const [addressCountryCode, setAddressCountryCode] = useState<string | null>(null);
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  const tenant = useQuery({
    queryKey: ['tenancy', 'current'],
    queryFn: () => tenancyApi.getCurrent(),
  });
  const members = useQuery({
    queryKey: ['administration', 'members'],
    queryFn: () => administrationApi.listMembers(),
  });
  const roles = useQuery({
    queryKey: ['authorization', 'roles'],
    queryFn: () => authorizationApi.listRoles(),
  });
  const denominations = useQuery({
    queryKey: ['tenancy', 'denominations'],
    queryFn: () => tenancyApi.listDenominations(),
  });
  const councils = useQuery({
    queryKey: ['tenancy', 'councils'],
    queryFn: () => tenancyApi.listCouncils(),
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['administration', 'members'] }),
      queryClient.invalidateQueries({ queryKey: ['tenancy', 'current'] }),
    ]);

  const updateProfile = useMutation({
    mutationFn: (profile: Parameters<typeof tenancyApi.updateProfile>[0]) =>
      tenancyApi.updateProfile(profile),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenancy', 'current'] }),
    onError: (error: unknown) => setActionError(describeError(error, t)),
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const tenantId = tenant.data!.id;
      const session = await filesApi.requestUpload({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        declaredSize: file.size,
        ownerType: 'Tenant',
        ownerId: tenantId,
      });
      await filesApi.putBytes(session.uploadUrl, file);
      const uploaded = await filesApi.completeUpload(session.id, {
        fileName: file.name,
        ownerType: 'Tenant',
        ownerId: tenantId,
      });
      return tenancyApi.updateProfile({ logoFileId: uploaded.id });
    },
    onSuccess: () => {
      setLogoError(null);
      return queryClient.invalidateQueries({ queryKey: ['tenancy', 'current'] });
    },
    onError: (error: unknown) => setLogoError(describeError(error, t)),
  });

  const updateRole = useMutation({
    mutationFn: ({ membershipId, roleKey }: { membershipId: string; roleKey: string }) =>
      administrationApi.updateMemberRole(membershipId, { roleKey }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['administration', 'members'] }),
    onError: (error: unknown) => {
      setActionError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  const removeMember = useMutation({
    mutationFn: (membershipId: string) => administrationApi.removeMember(membershipId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['administration', 'members'] }),
    onError: (error: unknown) => {
      setActionError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  const roleList = roles.data ?? [];
  const memberList = members.data ?? [];
  const canAdmin = can('tenancy.tenant.admin');

  const profileTabs: PersonTabDef[] = [
    { id: 'general', label: t('administration.tenant.tabs.general') },
    { id: 'location', label: t('administration.tenant.tabs.location') },
    { id: 'pastors', label: t('administration.tenant.tabs.pastors') },
  ];

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">{t('navigation.administration')}</h1>
        {can('people.customField.read') && (
          <Link className="btn" to="/administration/custom-fields">
            {t('people.customFields.manageLink')}
          </Link>
        )}
        {can('authorization.role.read') && (
          <Link className="btn" to="/administration/roles">
            {t('administration.roles.title')}
          </Link>
        )}
      </header>

      {tenant.data && (
        <div className="settings-card">
          <PersonTabs
            tabs={profileTabs}
            active={activeTab}
            onSelect={setActiveTab}
            label={t('navigation.administration')}
          />

          <form
            onSubmit={(event) => {
              event.preventDefault();
              const trimmedName = (name ?? tenant.data.name).trim();
              if (!trimmedName) return;

              const effectiveDenominationId = denominationId ?? tenant.data.denomination?.id ?? '';
              const effectiveCouncilId = councilId ?? tenant.data.council?.id ?? '';
              const effectiveLatitude = coordinates?.lat ?? tenant.data.address?.latitude ?? null;
              const effectiveLongitude = coordinates?.lng ?? tenant.data.address?.longitude ?? null;

              updateProfile.mutate({
                name: trimmedName,
                defaultLocale: defaultLocale ?? tenant.data.defaultLocale,
                defaultTimezone: defaultTimezone ?? tenant.data.defaultTimezone,
                defaultCurrency: defaultCurrency ?? tenant.data.defaultCurrency,
                denominationId: effectiveDenominationId || null,
                councilId: effectiveCouncilId || null,
                address: {
                  line1: (addressLine1 ?? tenant.data.address?.line1 ?? '').trim() || null,
                  line2: (addressLine2 ?? tenant.data.address?.line2 ?? '').trim() || null,
                  city: (addressCity ?? tenant.data.address?.city ?? '').trim() || null,
                  region: (addressRegion ?? tenant.data.address?.region ?? '').trim() || null,
                  postalCode:
                    (addressPostalCode ?? tenant.data.address?.postalCode ?? '').trim() || null,
                  countryCode:
                    (addressCountryCode ?? tenant.data.address?.countryCode ?? '')
                      .trim()
                      .toUpperCase() || null,
                  latitude: effectiveLatitude,
                  longitude: effectiveLongitude,
                },
              });
            }}
          >
            <PersonTabPanel id="general" active={activeTab}>
              <div className="tab-fieldset">
                <div className="field">
                  <span className="field-label">{t('common.labels.logo')}</span>
                  {canAdmin ? (
                    <label className="logo-upload" aria-label={t('common.labels.changeLogo')}>
                      <EntityLogo logoFileId={tenant.data.logoFileId} />
                      <span className="logo-upload-hint">{t('common.labels.changeLogo')}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          event.target.value = '';
                          if (file) uploadLogo.mutate(file);
                        }}
                      />
                    </label>
                  ) : (
                    <EntityLogo logoFileId={tenant.data.logoFileId} />
                  )}
                  {logoError && <Alert tone="danger">{logoError}</Alert>}
                </div>

                <Field
                  label={t('administration.tenant.name')}
                  name="tenantName"
                  value={name ?? tenant.data.name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={!canAdmin}
                />

                <div className="form-grid">
                  <div className="field">
                    <label className="field-label" htmlFor="tenantLocale">
                      {t('administration.tenant.locale')}
                    </label>
                    <select
                      id="tenantLocale"
                      className="field-input"
                      value={defaultLocale ?? tenant.data.defaultLocale}
                      onChange={(event) => setDefaultLocale(event.target.value)}
                      disabled={!canAdmin}
                    >
                      {SUPPORTED_LOCALES.map((entry) => (
                        <option key={entry.code} value={entry.code} dir={entry.direction}>
                          {entry.nativeName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="field">
                    <label className="field-label" htmlFor="tenantTimezone">
                      {t('administration.tenant.timezone')}
                    </label>
                    <select
                      id="tenantTimezone"
                      className="field-input"
                      value={defaultTimezone ?? tenant.data.defaultTimezone}
                      onChange={(event) => setDefaultTimezone(event.target.value)}
                      disabled={!canAdmin}
                    >
                      {TIMEZONES.map((zone) => (
                        <option key={zone} value={zone}>
                          {zone}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-grid">
                  <Field
                    label={t('administration.tenant.currency')}
                    name="tenantCurrency"
                    value={defaultCurrency ?? tenant.data.defaultCurrency}
                    onChange={(event) => setDefaultCurrency(event.target.value.toUpperCase())}
                    maxLength={3}
                    dir="ltr"
                    disabled={!canAdmin}
                  />

                  <CatalogSelect
                    id="tenantDenomination"
                    label={t('administration.tenant.denomination')}
                    value={denominationId ?? tenant.data.denomination?.id ?? ''}
                    current={tenant.data.denomination}
                    options={denominations.data ?? []}
                    onChange={setDenominationId}
                    disabled={!canAdmin}
                  />
                </div>

                <CatalogSelect
                  id="tenantCouncil"
                  label={t('administration.tenant.council')}
                  value={councilId ?? tenant.data.council?.id ?? ''}
                  current={tenant.data.council}
                  options={councils.data ?? []}
                  onChange={setCouncilId}
                  disabled={!canAdmin}
                />
              </div>
            </PersonTabPanel>

            <PersonTabPanel id="location" active={activeTab}>
              <fieldset className="tab-fieldset" disabled={!canAdmin}>
                <Field
                  label={t('people.person.addressLine1')}
                  name="addressLine1"
                  value={addressLine1 ?? tenant.data.address?.line1 ?? ''}
                  onChange={(event) => setAddressLine1(event.target.value)}
                />
                <Field
                  label={t('people.person.addressLine2')}
                  name="addressLine2"
                  value={addressLine2 ?? tenant.data.address?.line2 ?? ''}
                  onChange={(event) => setAddressLine2(event.target.value)}
                />
                <div className="form-grid">
                  <Field
                    label={t('people.person.addressCity')}
                    name="addressCity"
                    value={addressCity ?? tenant.data.address?.city ?? ''}
                    onChange={(event) => setAddressCity(event.target.value)}
                  />
                  <Field
                    label={t('people.person.addressRegion')}
                    name="addressRegion"
                    value={addressRegion ?? tenant.data.address?.region ?? ''}
                    onChange={(event) => setAddressRegion(event.target.value)}
                  />
                </div>
                <div className="form-grid">
                  <Field
                    label={t('people.person.addressPostalCode')}
                    name="addressPostalCode"
                    value={addressPostalCode ?? tenant.data.address?.postalCode ?? ''}
                    onChange={(event) => setAddressPostalCode(event.target.value)}
                    dir="ltr"
                  />
                  <Field
                    label={t('people.person.addressCountryCode')}
                    name="addressCountryCode"
                    value={addressCountryCode ?? tenant.data.address?.countryCode ?? ''}
                    onChange={(event) => setAddressCountryCode(event.target.value.toUpperCase())}
                    hint={t('people.person.addressCountryCodeHint')}
                    maxLength={2}
                    dir="ltr"
                  />
                </div>

                <LocationMapPicker
                  latitude={coordinates?.lat ?? tenant.data.address?.latitude ?? null}
                  longitude={coordinates?.lng ?? tenant.data.address?.longitude ?? null}
                  onChange={(lat, lng) => setCoordinates({ lat, lng })}
                />
              </fieldset>
            </PersonTabPanel>

            {canAdmin && activeTab !== 'pastors' && (
              <Button type="submit" variant="primary" loading={updateProfile.isPending}>
                {t('common.actions.save')}
              </Button>
            )}
          </form>

          <PersonTabPanel id="pastors" active={activeTab}>
            <PastorsTab canAdmin={canAdmin} />
          </PersonTabPanel>
        </div>
      )}

      {actionError && <Alert tone="danger">{actionError}</Alert>}

      <header className="page-header">
        <h2 className="page-title">{t('administration.member.title')}</h2>
        {canAdmin && (
          <Button variant="primary" onClick={() => setIsInviting(true)}>
            {t('administration.member.invite')}
          </Button>
        )}
      </header>

      {members.isPending && <Spinner label={t('common.states.loading')} />}
      {members.isSuccess && memberList.length === 0 && (
        <EmptyState title={t('administration.member.empty')} />
      )}

      {memberList.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('administration.member.email')}</th>
                <th scope="col">{t('administration.member.role')}</th>
                <th scope="col">{t('administration.member.status')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {memberList.map((member) => (
                <tr key={member.membershipId}>
                  <td>
                    {member.displayName ?? member.email}
                    {member.isOwner && (
                      <span className="cell-empty"> · {t('administration.member.owner')}</span>
                    )}
                  </td>
                  <td>
                    {canAdmin ? (
                      <select
                        className="field-input"
                        value={member.roles[0] ?? ''}
                        onChange={(event) =>
                          updateRole.mutate({
                            membershipId: member.membershipId,
                            roleKey: event.target.value,
                          })
                        }
                      >
                        {member.roles[0] && !roleList.some((r) => r.key === member.roles[0]) && (
                          <option value={member.roles[0]}>{member.roles[0]}</option>
                        )}
                        {roleList.map((role) => (
                          <option key={role.key} value={role.key}>
                            {role.name ?? t(role.nameKey, { fallback: role.key })}
                          </option>
                        ))}
                      </select>
                    ) : (
                      member.roles.map((roleKey) => roleLabel(roleKey, roleList, t)).join(', ')
                    )}
                  </td>
                  <td>
                    {t(`administration.status.${member.status}`, { fallback: member.status })}
                  </td>
                  <td>
                    {canAdmin && (
                      <Button
                        variant="ghost"
                        loading={
                          removeMember.isPending && removeMember.variables === member.membershipId
                        }
                        onClick={() => removeMember.mutate(member.membershipId)}
                      >
                        {t('administration.member.remove')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isInviting && (
        <InviteMemberDialog
          onClose={() => setIsInviting(false)}
          onInvited={() => {
            setIsInviting(false);
            void invalidate();
          }}
        />
      )}
    </div>
  );
}
